import { MessageCircle, Phone, Mail, Quote } from "lucide-react";
import { useLang } from "../lib/lang";
import { brandify } from "../components/Brand";
import { about } from "../lib/aboutCopy";
import { useSeo } from "../lib/seo";
import { PHONE, WA_DEFAULT } from "../lib/copy";
import { BUSINESS } from "../config/business";
import { trackWhatsApp, trackCall } from "../lib/analytics";
import { Reveal, Eyebrow, SectionTitle } from "../components/Reveal";
import { InkGrowth, InkClipboard, InkPeople, InkIdCard, InkSearch, InkWallet, InkDocument, InkShield, InkHeart, InkAward, InkCap } from "../components/InkIcons";

const AboutHero = ({ h }) => (
  <section className="relative overflow-hidden bg-ink pb-24 pt-20 text-white lg:pb-32 lg:pt-28" data-testid="about-hero">
    <div className="dot-grid pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
    <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-sun/90" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
      <Reveal><Eyebrow dark>{h.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}><h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="about-title">{brandify(h.title)}</h1></Reveal>
      <Reveal delay={0.1}><p className="mt-6 font-display text-xl font-bold text-sun sm:text-2xl" data-testid="about-tagline">{h.tagline}</p></Reveal>
      <Reveal delay={0.15}>
        <dl className="mt-12 flex max-w-xl flex-col divide-y divide-ink/10 overflow-hidden rounded-[1.5rem] bg-white text-ink shadow-2xl sm:flex-row sm:divide-x sm:divide-y-0" data-testid="about-stats">
          {h.stats.map(([v, l], i) => (
            <div key={v} className="flex-1 px-7 py-6" data-testid={`about-stat-${i}`}>
              <dt className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{v}</dt>
              <dd className="mt-1 text-sm font-semibold text-night/80">{l}</dd>
            </div>
          ))}
        </dl>
      </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-5">
        <figure data-testid="about-hero-image">
          <div className="overflow-hidden rounded-[2rem] border-4 border-white/90 shadow-2xl lg:rotate-2">
            <img src="/img/students-laptops.webp" alt={h.alt} className="aspect-[4/3] w-full object-cover" />
          </div>
          <figcaption className="mt-3 text-xs italic text-white/75">{h.imgNote}</figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const WhoWeAre = ({ w }) => (
  <section className="py-24 lg:py-32" data-testid="about-who">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-4"><Reveal><Eyebrow>{w.eyebrow}</Eyebrow></Reveal></div>
      <div className="lg:col-span-8">
        <Reveal><p className="font-display text-2xl font-extrabold leading-snug tracking-tight text-ink sm:text-3xl">{w.lead}</p></Reveal>
        <Reveal delay={0.08}><p className="mt-8 text-base leading-relaxed text-night sm:text-lg">{brandify(w.body)}</p></Reveal>
      </div>
    </div>
  </section>
);

const VM_ICONS = [InkGrowth, InkClipboard];
const VisionMission = ({ vm }) => (
  <section className="pb-24 lg:pb-32" data-testid="about-vision-mission">
    <div className="mx-auto grid max-w-7xl gap-5 px-5 sm:px-8 md:grid-cols-2">
      {vm.map((c, i) => {
        const Icon = VM_ICONS[i];
        const dark = i === 0;
        return (
          <Reveal key={c.label} delay={i * 0.08}>
            <article className={`h-full rounded-[2rem] p-8 sm:p-10 ${dark ? "bg-ink text-white" : "border border-ink/10 bg-sky text-ink"}`} data-testid={`about-vm-card-${i}`}>
              <span className={`flex h-16 w-16 items-center justify-center rounded-[1.1rem_1.5rem_1rem_1.6rem] ${dark ? "bg-white/10 text-white -rotate-3" : "bg-ink text-white rotate-3"}`}><Icon className="h-9 w-9" /></span>
              <h2 className={`mt-8 font-display text-2xl font-extrabold sm:text-3xl ${dark ? "text-sun" : "text-ink"}`}>{c.label}</h2>
              <p className={`mt-4 text-base leading-relaxed sm:text-lg ${dark ? "text-white/90" : "text-night"}`}>{c.text}</p>
            </article>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const SERVICE_ICONS = [InkPeople, InkIdCard, InkSearch, InkWallet, InkDocument];
const Services = ({ s }) => (
  <section className="bg-sky/60 py-24 lg:py-32" data-testid="about-services">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><Eyebrow>{s.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}><SectionTitle>{s.title}</SectionTitle></Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {s.items.map((it, i) => {
          const Icon = SERVICE_ICONS[i];
          return (
            <Reveal key={it.title} delay={i * 0.05}>
              <article className="group h-full rounded-[1.75rem] border border-ink/10 bg-white p-7 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(18,24,63,0.45)]" data-testid={`about-service-${i}`}>
                <span className={`flex h-14 w-14 items-center justify-center rounded-[1.1rem_1.4rem_1rem_1.5rem] bg-sky text-ink transition-transform duration-300 group-hover:rotate-0 ${i % 2 ? "rotate-2" : "-rotate-2"}`}><Icon className="h-8 w-8" /></span>
                <h3 className="mt-6 font-display text-xl font-extrabold text-ink">{it.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-night">{it.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Process = ({ p }) => (
  <section className="py-24 lg:py-32" data-testid="about-process">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><Eyebrow>{p.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}><SectionTitle className="max-w-3xl">{p.title}</SectionTitle></Reveal>
      <ol className="relative mt-16 grid gap-10 lg:grid-cols-5 lg:gap-6">
        <span className="absolute bottom-4 left-7 top-4 w-0.5 bg-ink/15 lg:bottom-auto lg:left-7 lg:right-[19%] lg:top-7 lg:h-0.5 lg:w-auto" aria-hidden="true" />
        {p.steps.map((st, i) => (
          <Reveal key={st.title} delay={i * 0.07}>
            <li className="relative flex gap-6 lg:block" data-testid={`about-step-${i + 1}`}>
              <span className={`relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full font-display text-xl font-extrabold ring-8 ring-white ${i === 4 ? "bg-sun text-night" : "bg-ink text-white"}`}>{String(i + 1).padStart(2, "0")}</span>
              <div className="lg:mt-6">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-night/70">{p.step} {i + 1}</p>
                <h3 className="mt-1 font-display text-lg font-extrabold text-ink">{st.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-night">{st.text}</p>
              </div>
            </li>
          </Reveal>
        ))}
      </ol>
      <p className="mt-12 text-sm text-night/80" data-testid="about-process-note">{p.note}</p>
    </div>
  </section>
);

const VALUE_ICONS = [InkShield, InkHeart, InkCap, InkAward];
const Values = ({ v }) => (
  <section className="relative overflow-hidden bg-ink py-24 text-white lg:py-32" data-testid="about-values">
    <div className="dot-grid pointer-events-none absolute inset-0 opacity-25" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal><Eyebrow dark>{v.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}><SectionTitle dark className="max-w-3xl">{v.title}</SectionTitle></Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {v.items.map((it, i) => {
          const Icon = VALUE_ICONS[i];
          return (
            <Reveal key={it.title} delay={i * 0.06}>
              <article className="h-full rounded-[1.75rem] border border-white/15 bg-white/5 p-7 backdrop-blur" data-testid={`about-value-${i}`}>
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sun text-night"><Icon className="h-8 w-8" accent="#FFFFFF" /></span>
                <h3 className="mt-6 font-display text-xl font-extrabold text-white">{it.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-white/85">{it.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Founder = ({ f }) => (
  <section className="py-24 lg:py-32" data-testid="about-founder">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal><Eyebrow>{f.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <figure className="relative mt-8 rounded-[2rem] border border-ink/10 bg-white p-8 shadow-[0_30px_60px_-40px_rgba(18,24,63,0.5)] sm:p-12">
          <Quote className="h-10 w-10 text-sun" aria-hidden="true" />
          <blockquote className="mt-6 font-display text-xl font-bold leading-relaxed text-ink sm:text-2xl" data-testid="about-founder-note">{f.text}</blockquote>
          <figcaption className="mt-10 flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ink font-display text-lg font-extrabold text-sun" aria-hidden="true">RP</span>
            <span>
              <span className="block font-display text-lg font-extrabold text-ink" data-testid="about-founder-name">{f.name}</span>
              <span className="block text-sm font-semibold text-night/80">{brandify(f.role)}</span>
            </span>
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const AboutCta = ({ c }) => (
  <section className="relative overflow-hidden bg-sun py-24 text-night lg:py-28" data-testid="about-cta">
    <div className="relative mx-auto max-w-4xl px-5 sm:px-8 sm:text-center">
      <Reveal><h2 className="font-display text-3xl font-extrabold leading-[1.1] tracking-tight text-ink sm:text-5xl">{c.title}</h2></Reveal>
      <Reveal delay={0.05}><p className="mt-6 text-base leading-relaxed sm:text-lg">{brandify(c.text)}</p></Reveal>
      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
        <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("about-cta")} data-testid="about-cta-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-display font-extrabold text-white transition-colors hover:bg-night"><MessageCircle className="h-5 w-5 text-sun" aria-hidden="true" />{c.button}</a>
        <a href={`tel:+91${PHONE}`} onClick={() => trackCall("about-cta")} data-testid="about-cta-call-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-display font-extrabold text-ink transition-colors hover:bg-sky"><Phone className="h-5 w-5" aria-hidden="true" />{c.call} {PHONE}</a>
        <a href={`mailto:${BUSINESS.email}`} data-testid="about-cta-email-link" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink px-8 py-4 font-display font-extrabold text-ink transition-colors hover:bg-ink hover:text-white"><Mail className="h-5 w-5" aria-hidden="true" />{BUSINESS.email}</a>
      </div>
    </div>
  </section>
);

export default function About() {
  const { lang } = useLang();
  const a = about[lang] ?? about.en;
  useSeo({ ...about.en.seo, path: "/about" });
  return (
    <main data-testid="about-page">
      <AboutHero h={a.hero} />
      <WhoWeAre w={a.who} />
      <VisionMission vm={a.vm} />
      <Services s={a.services} />
      <Process p={a.process} />
      <Values v={a.values} />
      <Founder f={a.founder} />
      <AboutCta c={a.cta} />
    </main>
  );
}
